// Globals
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { productsSelector, updateProductSeo } from '../../feature';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Components
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledContentBlock,
  StyledSaveChangesButton,
} from './ProductDetailsSeoContainerStyles';
import { showErrorToast, showSuccessToast, useForm, HasUnsavedChangesModal } from '@/components';
import { ProductSeoForm } from '../../components';

// Helper
import { IProductTabContentProps, getSeoInitialLocale } from '../../helpers';
import { useHasUnsavedChangesModal } from '@/hooks';
import { UpdateProductSeoDTO } from '../../dtos';

const ProductDetailsSeoContainer = ({
  hasUnsavedChanges,
  pendingTab,
  handleConfirmTabSwitch,
  setHasUnsavedChanges,
  clearUnsavedChanges,
  handleDeclineTabSwitch,
}: IProductTabContentProps) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const { id } = useParams();
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);
  const { isOpened, onCloseModal, onConfirmTabSwitchAndCloseModal, handleValuesChange } = useHasUnsavedChangesModal({
    hasUnsavedChanges,
    canModalOpen: !!pendingTab,
    handleConfirmTabSwitch,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    handleDeclineTabSwitch,
    isTrackingEnabled: canManageProduct,
  });

  const { isLoading, product } = useAppState(productsSelector);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const updateSeoDto = new UpdateProductSeoDTO(values);
      await dispatch(updateProductSeo({ id: id as string, data: updateSeoDto }));
      showSuccessToast({ message: t('data_saved') });
      clearUnsavedChanges();
    } catch (error) {
      showErrorToast({ message: t('product_update_validation_error') });
    }
  };

  const handleSaveAndClose = async () => {
    await handleSubmit();
    onConfirmTabSwitchAndCloseModal();
  };

  useEffect(() => {
    if (product?.locale) {
      form.setFieldsValue(getSeoInitialLocale(product?.locale));
    }
  }, [product?.locale, form]);

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledContainerTitle>{t('seo_tab_title')}</StyledContainerTitle>
        {canManageProduct && (
          <StyledSaveChangesButton htmlType="submit" onClick={handleSubmit}>
            {t('product_details_save_changes_button')}
          </StyledSaveChangesButton>
        )}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <ProductSeoForm
          handleValuesChange={handleValuesChange}
          formInstance={form}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </StyledContentBlock>
      <HasUnsavedChangesModal
        isOpened={isOpened}
        handleCloseModal={onCloseModal}
        handleSaveAndClose={handleSaveAndClose}
        handleExitAndClose={onConfirmTabSwitchAndCloseModal}
      />
    </StyledContainerWrapper>
  );
};

export default ProductDetailsSeoContainer;
